import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@rekonas/auth";
import { Alert, Button, Input, LoginShell, Spinner } from "@rekonas/ui";

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [identity, setIdentity] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const destination =
    (location.state as LocationState | null)?.from || "/recordings";

  useEffect(() => {
    document.title = "Sign in · Somnoscope";
  }, []);

  if (!auth.isRestoring && auth.isAuthenticated)
    return <Navigate to={destination} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(undefined);
    try {
      await auth.login(identity, password);
      navigate(destination, { replace: true });
    } catch {
      setError("Sign-in failed. Check your credentials and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <LoginShell>
      <section
        className="w-[min(92vw,390px)] rounded-xl border border-[rgba(255,255,255,0.72)] bg-white/[0.61] px-[30px] pb-[29px] pt-[31px] shadow-[0_22px_55px_rgba(36,63,71,0.16)] backdrop-blur-[1px] max-sm:px-[22px] max-sm:pb-[25px] max-sm:pt-[27px]"
        aria-labelledby="login-heading"
      >
        {/* <p className="text-center text-xs font-bold uppercase tracking-[0.18em] text-teal-700">
          Rekonas
        </p> */}
        <h1
          id="login-heading"
          className="mt-2 text-center text-[27px] font-semibold leading-[1.15] tracking-[-0.025em] text-ink"
        >
          Sign in to your
          <br />
          Neoma workspace
        </h1>
        <p className="mt-2 text-center text-[13px] leading-5 text-slate-600">
          Use your existing Rekonas identity.{" "}
        </p>

        <form className="mt-6 grid gap-2" onSubmit={submit}>
          <div className="grid gap-2">
            <label
              className="mt-[5px] block text-xs font-normal text-[#48616a]"
              htmlFor="identity"
            >
              Email
            </label>
            <Input
              className="!h-[43px] !rounded-[5px] !border-[rgba(142,172,180,0.34)] !bg-[rgba(246,250,252,0.91)] !px-3 !text-[#263c44] !shadow-[inset_0_1px_2px_rgba(28,54,62,0.04)] focus:!border-[rgba(0,152,136,0.65)] focus:!ring-[3px] focus:!ring-[rgba(0,152,136,0.1)]"
              id="identity"
              name="identity"
              autoComplete="username"
              value={identity}
              onChange={(event) => setIdentity(event.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="grid gap-2">
            <label
              className="mt-[5px] block text-xs font-normal text-[#48616a]"
              htmlFor="password"
            >
              Password
            </label>
            <Input
              className="!h-[43px] !rounded-[5px] !border-[rgba(142,172,180,0.34)] !bg-[rgba(246,250,252,0.91)] !px-3 !text-[#263c44] !shadow-[inset_0_1px_2px_rgba(28,54,62,0.04)] focus:!border-[rgba(0,152,136,0.65)] focus:!ring-[3px] focus:!ring-[rgba(0,152,136,0.1)]"
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </div>
          {error && (
            <Alert className="border-red-200 bg-red-50 text-red-900">
              {error}
            </Alert>
          )}
          <Button
            className="mt-[27px] w-full !h-[43px] !rounded-[5px] !bg-[#009888] !font-semibold !shadow-[0_7px_17px_rgba(0,152,136,0.17)] hover:!bg-[#009888]"
            size="lg"
            type="submit"
            disabled={submitting || auth.isRestoring}
          >
            {(submitting || auth.isRestoring) && <Spinner />}
            {submitting
              ? "Signing in…"
              : auth.isRestoring
                ? "Restoring session…"
                : "Sign in"}
          </Button>
        </form>
      </section>
    </LoginShell>
  );
}
